import { datadogRum } from '@datadog/browser-rum';
import * as lscache from 'lscache';
import allSettled from 'promise.allsettled';

import { REST_COUNTRIES_KEY } from '../common/CountryCodesMap';
import { getTenant, GXB, GXS } from '../common/Tenants';
import { BETA_KEY, FEATURE_FLAGS, ISADMINKEY } from '../constants';
import Service, {
  BackendService,
  City,
  Country,
  Facet,
  Strategy,
  TechFamily,
  TFRole,
  Vehicle,
} from '../lib/App';
import { fetchFeatureFlags } from '../lib/Featureflag';
import { DomainMetadata, Variables } from '../lib/PlannerDTOs';
import { RoleName } from '@/common/const/Auth';
import { GoogleInfo } from '@/common/dto/Login';
import {
  isDev,
  isLocal,
  isMultiEnvEnabled,
  isProduction,
  isStaging,
  SEGP_WHITELIST_TOKEN_PROD,
  SEGP_WHITELIST_TOKEN_STG,
} from '@/common/environment';
import { TreeData } from '@/common/selects/CommonTreeSelect';
import { SystemEnvironment } from '@/constants/systemEnviroment';
import { ConcedoToken } from '@/utils/concedoAuthHelper';
import { getSystemEnvironment } from '@/utils/system-environment';

export interface BasicUser {
  id: number;
  email: string;
}

/**
 * Represents a cache service for common data.
 *
 * @export
 * @class CacheService
 */
export class CacheService {
  // Various caches
  private countries: Map<string, Country> = new Map<string, Country>();
  private countriesPrd: Map<string, Country> = new Map<string, Country>();
  private countriesMap: { [key: string]: any } = {};
  private countriesMapPrd: { [key: string]: any } = {};
  private cities: Map<number, City> = new Map<number, City>();
  private citiesPrd: Map<number, City> = new Map<number, City>();
  private variables: Map<any, any> = new Map<string, any>();
  private vehicles: Map<number, Vehicle> = new Map<number, Vehicle>();
  private vehiclesPrd: Map<number, Vehicle> = new Map<number, Vehicle>();
  private facets: Map<string, Facet> = new Map<string, Facet>();
  private facetsPrd: Map<string, Facet> = new Map<string, Facet>();
  private strategies: Map<string, Strategy> = new Map<string, Strategy>();
  private strategiesPrd: Map<string, Strategy> = new Map<string, Strategy>();
  private segpNamespace: Map<string, any> = new Map<string, any>();
  private segpNamespacePrd: Map<string, any> = new Map<string, any>();
  private backend: Map<string, BackendService> = new Map<
    string,
    BackendService
  >();
  private backendPrd: Map<string, BackendService> = new Map<
    string,
    BackendService
  >();
  private tags: Map<string, string> = new Map<string, string>();
  private tagsPrd: Map<string, string> = new Map<string, string>();
  private techFamily: Map<string, TechFamily> = new Map<string, TechFamily>();
  private techFamilyPrd: Map<string, TechFamily> = new Map<
    string,
    TechFamily
  >();
  private domainMetadata: Map<string, DomainMetadata> = new Map<
    string,
    DomainMetadata
  >();
  private domainMetadataPrd: Map<string, DomainMetadata> = new Map<
    string,
    DomainMetadata
  >();
  private variableTypes: Map<string, string> = new Map<string, string>();
  private variableTypesPrd: Map<string, string> = new Map<string, string>();
  private users: BasicUser[] = [];
  private countryCitiesMap: Map<string, number[]> = new Map<string, number[]>();
  private countryCitiesMapPrd: Map<string, number[]> = new Map<
    string,
    number[]
  >();
  private cityVehiclesMap: Map<number, number[]> = new Map<number, number[]>();
  private cityVehiclesMapPrd: Map<number, number[]> = new Map<
    number,
    number[]
  >();
  private countryVehicles: Map<string, number[]> = new Map<string, number[]>();
  private countryVehiclesPrd: Map<string, number[]> = new Map<
    string,
    number[]
  >();
  private userInfo: ConcedoToken | null;
  private ssoUserInfo: GoogleInfo | null;

  public async initialize(email: string): Promise<void> {
    // Promise.allSettled
    const featureFlagsPromise = fetchFeatureFlags()
      .then((flags: Map<string, boolean>) => {
        Object.keys(flags).forEach((k) => {
          sessionStorage.setItem(k, flags[k]);
        });
      })
      .catch((e) => {
        console.error(e);
      });

    const environmentCachesPromise = featureFlagsPromise.then(async () => {
      await allSettled([
        ...this.loadEnvironmentCaches(
          SystemEnvironment.STAGING,
          this.backend,
          this.techFamily,
          this.strategies,
          this.facets,
          this.variableTypes,
          this.domainMetadata,
          this.tags,
          this.segpNamespace,
        ),
        ...this.loadEnvironmentCaches(
          SystemEnvironment.PRODUCTION,
          this.backendPrd,
          this.techFamilyPrd,
          this.strategiesPrd,
          this.facetsPrd,
          this.variableTypesPrd,
          this.domainMetadataPrd,
          this.tagsPrd,
          this.segpNamespacePrd,
        ),
      ]);
    });

    const finalPromises = [
      Service.planner!.fetchBasicUsers().then((users: any[]) => {
        this.users = users;
      }),

      this.fetchAndCacheOrGetFromCache<Variables>(
        'variables',
        Service.planner.fetchVariables,
      ).then((variables) => {
        this.variables.set('variables', variables);
      }),

      featureFlagsPromise,

      // Add session cache if user is admin
      featureFlagsPromise.then(() => this.setToCacheIsAdmin(email)),

      environmentCachesPromise,
    ];

    await allSettled(finalPromises);
  }

  private async setCountryCitiesVehicles() {
    await this.fetchAndCacheOrGetFromCache<Country[]>(
      'countries',
      Service.planner.getCountries,
      SystemEnvironment.STAGING,
    ).then(async (countries: Country[]) => {
      const cMap: { [key: string]: any } = {
        REST: {
          type: 'optgroup',
          value: 'Others',
          children: {},
        },
      };

      countries?.forEach((country: Country) => {
        this.countries.set(country.Code, country);

        if (country?.IsOperationalCountry) cMap[country.Code] = country.Name;
        else cMap[REST_COUNTRIES_KEY].children[country.Code] = country.Name;
      });

      this.countriesMap = cMap;
    });

    await this.fetchAndCacheOrGetFromCache<City[]>(
      'cities',
      Service.planner!.fetchCities,
      SystemEnvironment.STAGING,
    ).then(async (cities: City[]) => {
      Object.keys(this.countriesMap).forEach((countryCode) => {
        if (!this.countryCitiesMap.has(countryCode))
          this.countryCitiesMap.set(countryCode, []);
      });

      cities.forEach((city: City) => {
        this.cities.set(city.id, city);

        if (
          this.countryCitiesMap.has(city.countryCode) &&
          !this.countryCitiesMap.get(city.countryCode).includes(city.id)
        )
          this.countryCitiesMap.get(city.countryCode).push(city.id);

        if (!this.cityVehiclesMap.has(city.id))
          this.cityVehiclesMap.set(city.id, []);
      });

      const vehicles: Vehicle[] = await Service.planner!.fetchAllVehicles(
        SystemEnvironment.STAGING,
      );

      vehicles
        .filter(({ cityID }: Vehicle): boolean => {
          return !!this.cities.get(cityID);
        })
        .forEach((vehicle: Vehicle) => {
          this.vehicles.set(vehicle.id, vehicle);

          if (
            this.cityVehiclesMap.has(vehicle.cityID) &&
            !this.cityVehiclesMap.get(vehicle.cityID).includes(vehicle.id)
          )
            this.cityVehiclesMap.get(vehicle.cityID).push(vehicle.id);
        });

      this.countryCitiesMap.forEach(
        (countryCities: number[], countryCode: string) => {
          let allVehicles = [];

          countryCities.forEach((cityId) => {
            allVehicles = [
              ...(this.cityVehiclesMap.get(cityId) || []),
              ...allVehicles,
            ];
          });
          this.countryVehicles.set(countryCode, allVehicles);
        },
      );
    });
  }

  private async setCountryCitiesVehiclesPrd() {
    await this.fetchAndCacheOrGetFromCache<Country[]>(
      'countries',
      Service.planner.getCountries,
      SystemEnvironment.PRODUCTION,
    ).then(async (countries: Country[]) => {
      const cMap: { [key: string]: any } = {
        REST: {
          type: 'optgroup',
          value: 'Others',
          children: {},
        },
      };

      countries?.forEach((country: Country) => {
        this.countriesPrd.set(country.Code, country);

        if (country?.IsOperationalCountry) cMap[country.Code] = country.Name;
        else cMap[REST_COUNTRIES_KEY].children[country.Code] = country.Name;
      });

      this.countriesMapPrd = cMap;
    });

    await this.fetchAndCacheOrGetFromCache<City[]>(
      'cities',
      Service.planner!.fetchCities,
      SystemEnvironment.PRODUCTION,
    ).then(async (cities: City[]) => {
      Object.keys(this.countriesMapPrd).forEach((countryCode) => {
        if (!this.countryCitiesMapPrd.has(countryCode))
          this.countryCitiesMapPrd.set(countryCode, []);
      });

      cities.forEach((city: City) => {
        this.citiesPrd.set(city.id, city);

        if (
          this.countryCitiesMapPrd.has(city.countryCode) &&
          !this.countryCitiesMapPrd.get(city.countryCode).includes(city.id)
        )
          this.countryCitiesMapPrd.get(city.countryCode).push(city.id);

        if (!this.cityVehiclesMapPrd.has(city.id))
          this.cityVehiclesMapPrd.set(city.id, []);
      });

      const vehicles: Vehicle[] = await Service.planner!.fetchAllVehicles(
        SystemEnvironment.PRODUCTION,
      );

      vehicles
        .filter(({ cityID }: Vehicle): boolean => {
          return !!this.citiesPrd.get(cityID);
        })
        .forEach((vehicle: Vehicle) => {
          this.vehiclesPrd.set(vehicle.id, vehicle);

          if (
            this.cityVehiclesMapPrd.has(vehicle.cityID) &&
            !this.cityVehiclesMapPrd.get(vehicle.cityID).includes(vehicle.id)
          )
            this.cityVehiclesMapPrd.get(vehicle.cityID).push(vehicle.id);
        });

      this.countryCitiesMapPrd.forEach(
        (countryCities: number[], countryCode: string) => {
          let allVehicles = [];

          countryCities.forEach((cityId) => {
            allVehicles = [
              ...(this.cityVehiclesMapPrd.get(cityId) || []),
              ...allVehicles,
            ];
          });
          this.countryVehiclesPrd.set(countryCode, allVehicles);
        },
      );
    });
  }

  // sets to session cache if user is admin
  private async setToCacheIsAdmin(email: string) {
    let isAdmin: boolean = false;
    const isServiceAccount = email.startsWith('svc.');
    const rolesPromise = this.isFeatureFlagOn(FEATURE_FLAGS.SERVICE_AUTHZ)
      ? Service.planner!.fetchUserRoles().then((userRoles) => {
          isAdmin =
            !isServiceAccount &&
            Array.isArray(userRoles) &&
            userRoles.some((ur) => ur?.roleName === RoleName.ADMIN);
        })
      : Service.planner!.fetchUserTFRoles().then((userTFRoles: TFRole[]) => {
          isAdmin =
            !isServiceAccount &&
            Array.isArray(userTFRoles) &&
            userTFRoles.some((ur) => ur?.role === RoleName.ADMIN);
        });

    await rolesPromise
      .catch((e) => {
        console.error(e);
      })
      .finally(() => {
        const k = this.getIsAdminSessionKey();
        sessionStorage.setItem(k, isAdmin.toString());
      });
  }

  private loadEnvironmentCaches(
    env: SystemEnvironment,
    backendMap: Map<string, BackendService>,
    techFamilyMap: Map<string, TechFamily>,
    strategyMap: Map<string, Strategy>,
    facetMap: Map<string, Facet>,
    variableTypeMap: Map<string, string>,
    domainMetadataMap: Map<string, DomainMetadata>,
    tagMap: Map<string, string>,
    segpNamespaceMap: Map<string, any>,
  ) {
    if (
      env === SystemEnvironment.STAGING &&
      !isMultiEnvEnabled() &&
      !isStaging &&
      !isDev &&
      !isLocal
    ) {
      return [];
    } else if (
      env === SystemEnvironment.PRODUCTION &&
      !isMultiEnvEnabled() &&
      !isProduction
    ) {
      return [];
    } else {
      const promises = [];

      if (!this.shouldSkipLegacyBackendCache()) {
        promises.push(
          this.fetchAndCacheOrGetFromCache<BackendService[]>(
            'backend',
            Service.planner!.fetchBackendServices,
            env,
          ).then((backendServices: BackendService[]) => {
            backendServices.forEach((backendService: BackendService) => {
              backendMap.set(backendService.prefix, backendService);
            });
          }),
        );
      }

      if (!this.shouldSkipLegacyTechFamilyCache()) {
        promises.push(
          this.fetchAndCacheOrGetFromCache<TechFamily[]>(
            'techFamily',
            Service.planner!.fetchTechFamilies,
            env,
          ).then((techfamilies: TechFamily[]) => {
            techfamilies.forEach((techFamily: TechFamily) => {
              techFamilyMap.set(techFamily.prefix, techFamily);
            });
          }),
        );
      }

      return [
        ...promises,
        this.fetchAndCacheOrGetFromCache<Strategy[]>(
          'strategies',
          Service.planner!.fetchStrategies,
          env,
        ).then((strategies: Strategy[]) => {
          strategies.forEach((strategy: Strategy) => {
            strategyMap.set(strategy.name, strategy);
          });
        }),

        env === SystemEnvironment.STAGING
          ? this.setCountryCitiesVehicles()
          : this.setCountryCitiesVehiclesPrd(),

        this.fetchAndCacheOrGetFromCache<Facet[]>(
          'facets',
          Service.planner!.fetchFacets,
          env,
        ).then((facets: Facet[]) => {
          facets.forEach((facet: Facet) => {
            facetMap.set(facet.name, facet);
          });
        }),

        this.fetchAndCacheOrGetFromCache<string[]>(
          'vtype',
          Service.planner!.fetchVariableTypes,
          env,
        ).then((variableTypes: string[]) => {
          variableTypes.forEach((variableType: string) => {
            variableTypeMap.set(
              variableType.toString(),
              variableType.toString(),
            );
          });
        }),

        this.fetchAndCacheOrGetFromCache<DomainMetadata[]>(
          'domainMetadata',
          Service.planner!.fetchDomainMetadata,
          env,
        ).then((domainMetadata: DomainMetadata[]) => {
          domainMetadata.forEach((entry: DomainMetadata) => {
            domainMetadataMap.set(entry.name, entry);
          });
        }),

        this.fetchAndCacheOrGetFromCache<string[]>(
          'experimentTags',
          Service.planner!.fetchAllExperimentTags,
          env,
        ).then((tags: string[]) => {
          tags.forEach((tag: string) => {
            tagMap.set(tag, tag);
          });
        }),

        //Don't trigger SEGP call for GXS or GXB tenants - Production
        (() => {
          const currentTenant = getTenant();
          if (currentTenant === GXS || currentTenant === GXB) {
            segpNamespaceMap.set('namespaces', null);
            segpNamespaceMap.set('overrideNamespaces', null);
            return Promise.resolve();
          }

          return Service.planner
            .getSegpNamespace(
              env,
              env === SystemEnvironment.PRODUCTION
                ? SEGP_WHITELIST_TOKEN_PROD
                : SEGP_WHITELIST_TOKEN_STG,
            )
            .then((res) => {
              if (res.ok) {
                return res.json();
              } else {
                throw new Error('Something went wrong');
              }
            })
            .then((data) => {
              if (data?.capabilityMap.namespaces)
                segpNamespaceMap.set(
                  'namespaces',
                  JSON.parse(data?.capabilityMap.namespaces),
                );
              else {
                segpNamespaceMap.set('namespaces', null);
              }

              if (data?.capabilityMap.overrideNamespaces)
                segpNamespaceMap.set(
                  'overrideNamespaces',
                  JSON.parse(data?.capabilityMap.overrideNamespaces),
                );
              else {
                segpNamespaceMap.set('overrideNamespaces', null);
              }
            })
            .catch((e) => {
              console.error(
                'Failed to fetch SEGP namespace for production:',
                e,
              );
              if (segpNamespaceMap) {
                segpNamespaceMap.set('namespaces', null);
                segpNamespaceMap.set('overrideNamespaces', null);
              }
            });
        })(),
      ];
    }
  }

  private shouldSkipLegacyBackendCache(): boolean {
    return (
      this.isFeatureFlagOn(FEATURE_FLAGS.SERVICE_AUTHZ) &&
      this.isFeatureFlagOn(FEATURE_FLAGS.INCLUDE_HELIX_SERVICES)
    );
  }

  private shouldSkipLegacyTechFamilyCache(): boolean {
    return (
      this.isFeatureFlagOn(FEATURE_FLAGS.SERVICE_AUTHZ) &&
      this.isFeatureFlagOn(FEATURE_FLAGS.INCLUDE_HELIX_TECH_FAMILIES)
    );
  }

  // Get gets or fetches the value.
  private async fetchAndCacheOrGetFromCache<T>(
    type: string,
    fetch: (k?: any) => Promise<T>,
    key?: any,
  ): Promise<T> {
    const k: string = key ? type + '.' + key : type;
    const v: T = lscache.get(k) as T;
    if (v) {
      return new Promise<T>((resolve) => {
        resolve(v);
      });
    }

    // If we missed, return the
    const value: T = await fetch(key);
    if (value) {
      lscache.set(k, value, 1440); // cache for 1 day
    }

    // Resolve
    return new Promise<T>((resolve) => {
      resolve(value);
    });
  }

  /**
   * Gets all vehicles.
   */
  public getAllVehicles(): Vehicle[] {
    const env = getSystemEnvironment();
    if (env === SystemEnvironment.STAGING) {
      return Array.from(this.vehicles.values());
    } else {
      return Array.from(this.vehiclesPrd.values());
    }
  }

  /**
   * Gets the vehicle by its id.
   * @param id
   */
  public getVehicle(id: number): Vehicle | undefined {
    const env = getSystemEnvironment();
    if (env === SystemEnvironment.STAGING) {
      return this.vehicles.get(id);
    } else {
      return this.vehiclesPrd.get(id);
    }
  }

  /**
   * Gets all countries.
   */
  public getAllCountries(): Country[] {
    const env = getSystemEnvironment();
    if (env === SystemEnvironment.STAGING) {
      return Array.from(this.countries.values());
    } else {
      return Array.from(this.countriesPrd.values());
    }
  }

  public getCountriesMap() {
    const env = getSystemEnvironment();
    if (env === SystemEnvironment.STAGING) {
      return this.countriesMap;
    } else {
      return this.countriesMapPrd;
    }
  }

  /**
   * Gets all cities.
   */
  public getAllCities(): City[] {
    const env = getSystemEnvironment();
    if (env === SystemEnvironment.STAGING) {
      return Array.from(this.cities.values());
    } else {
      return Array.from(this.citiesPrd.values());
    }
  }

  /**
   * Gets the city by its id.
   * @param id
   */
  public getCity(id: number): City | undefined {
    const env = getSystemEnvironment();
    if (env === SystemEnvironment.STAGING) {
      return this.cities.get(id);
    } else {
      return this.citiesPrd.get(id);
    }
  }

  /**
   * Gets all facets.
   */
  public getAllFacets(): Facet[] {
    const env = getSystemEnvironment();
    if (env === SystemEnvironment.STAGING) {
      return Array.from(this.facets.values());
    } else {
      return Array.from(this.facetsPrd.values());
    }
  }

  /**
   * Gets the facet by its name.
   * @param name
   */
  public getFacet(name: string): Facet | undefined {
    const env = getSystemEnvironment();
    if (env === SystemEnvironment.STAGING) {
      return this.facets.get(name);
    } else {
      return this.facetsPrd.get(name);
    }
  }

  /**
   * Gets all strategies.
   */
  public getAllStrategies(): Strategy[] {
    const env = getSystemEnvironment();
    if (env === SystemEnvironment.STAGING) {
      return Array.from(this.strategies.values());
    } else {
      return Array.from(this.strategiesPrd.values());
    }
  }

  /**
   * Gets the strategy by its name.
   * @param name
   */
  public getStrategy(name: string): Strategy | undefined {
    const env = getSystemEnvironment();
    if (env === SystemEnvironment.STAGING) {
      return this.strategies.get(name);
    } else {
      return this.strategiesPrd.get(name);
    }
  }

  /**
   * Get country by vehicle
   * @param { number } id
   *
   * @return { string } countryCode
   */
  public getCountryByVehicle(id: number): string {
    const vehicle: Vehicle = Service.cache!.getVehicle(id)!;
    const city: City = Service.cache!.getCity(vehicle.cityID)!;
    return city.countryCode.toUpperCase();
  }

  public getAllBackendServices(): BackendService[] {
    const env = getSystemEnvironment();

    if (env === SystemEnvironment.STAGING) {
      return Array.from(this.backend.values());
    } else {
      return Array.from(this.backendPrd.values());
    }
  }

  public getBackendServiceLabel(prefix: string): string {
    const res = this.getAllBackendServices().find(
      (backend) => backend.prefix === prefix,
    );

    return res?.title || '';
  }

  public getAllTechFamilies(): TechFamily[] {
    const env = getSystemEnvironment();

    if (env === SystemEnvironment.STAGING) {
      return Array.from(this.techFamily.values());
    } else {
      return Array.from(this.techFamilyPrd.values());
    }
  }

  public getTechFamilyLabel(prefix: string): string {
    const res = this.getAllTechFamilies().find(
      (techFamily) => techFamily.prefix === prefix,
    );

    return res?.title || '';
  }

  public getAllVariables() {
    return this.variables.get('variables')?.items || [];
  }

  public getAllVariableTypes(): string[] {
    const env = getSystemEnvironment();
    if (env === SystemEnvironment.STAGING) {
      return Array.from(this.variableTypes.values());
    } else {
      return Array.from(this.variableTypesPrd.values());
    }
  }

  public getBasicUsers(): BasicUser[] {
    return this.users;
  }

  public getAllDomainMetadata(): DomainMetadata[] {
    const env = getSystemEnvironment();
    if (env === SystemEnvironment.STAGING) {
      return Array.from(this.domainMetadata.values());
    } else {
      return Array.from(this.domainMetadataPrd.values());
    }
  }

  public getDomainLabel(name: string): string {
    const res = this.getAllDomainMetadata().find(
      (domain) => domain.name === name,
    );

    return res?.title;
  }

  public getAllExperimentTags(): any {
    return Array.from(this.tags.values());
  }

  /**
   * Returns true or false for a feature flag
   * @param flag
   */
  public isFeatureFlagOn(flag: string): boolean {
    return sessionStorage.getItem(flag)
      ? JSON.parse(sessionStorage.getItem(flag))
      : false;
  }

  public isDomainOverlapSupported(domain: string): boolean {
    if (!domain) {
      return false;
    }

    const domainMetadata = this.getAllDomainMetadata().find(
      (metadata) => metadata.name === domain,
    );

    // Domain supports overlap if it exists in metadata and has overlapping: true
    return domainMetadata?.overlapping === true;
  }

  public isTimebasedBackdoor(): boolean {
    const flag: string = 'allow_time_overlap';
    return sessionStorage.getItem(flag)
      ? JSON.parse(sessionStorage.getItem(flag))
      : false;
  }

  public isUserAdmin(): boolean {
    const k = this.getIsAdminSessionKey();
    return sessionStorage.getItem(k)
      ? JSON.parse(sessionStorage.getItem(k))
      : false;
  }

  public isBetaFeatureEnabled(): boolean {
    return (sessionStorage.getItem(BETA_KEY) ?? 'false') == 'true';
  }

  public setIsBetaFeatureEnabled(flag: boolean) {
    sessionStorage.setItem(BETA_KEY, flag ? 'true' : 'false');
  }

  private getIsAdminSessionKey(): string {
    return btoa(ISADMINKEY);
  }

  public getCitiesByCountryCode(countryCode: string): number[] {
    const env = getSystemEnvironment();
    if (env === SystemEnvironment.STAGING) {
      return this.countryCitiesMap.get(countryCode);
    } else {
      return this.countryCitiesMapPrd.get(countryCode);
    }
  }

  public getVehiclesByCountryCode(countryCode: string): number[] {
    const env = getSystemEnvironment();
    if (env === SystemEnvironment.STAGING) {
      return this.countryVehicles.get(countryCode);
    } else {
      return this.countryVehiclesPrd.get(countryCode);
    }
  }

  public getVehiclesByCityID(cityID: number): number[] {
    const env = getSystemEnvironment();
    if (env === SystemEnvironment.STAGING) {
      return this.cityVehiclesMap.get(cityID);
    } else {
      return this.cityVehiclesMapPrd.get(cityID);
    }
  }

  public getSegpNamespaces(): string[] {
    const env = getSystemEnvironment();
    if (env === SystemEnvironment.STAGING) {
      return this.segpNamespace.get('namespaces');
    } else {
      return this.segpNamespacePrd.get('namespaces');
    }
  }

  public getOverrideSegpNamespaces(): { [key: string]: any } {
    const env = getSystemEnvironment();
    if (env === SystemEnvironment.STAGING) {
      return this.segpNamespace.get('overrideNamespaces');
    } else {
      return this.segpNamespacePrd.get('overrideNamespaces');
    }
  }

  public getCityCountryTreeData(multiple: boolean = false): TreeData[] {
    const treeData: TreeData[] = [];
    const cities = Service.cache!.getAllCities();
    // .filter(({ name }) =>
    //   onlyLettersAndSpaces.test(name),
    // );
    // .filter(({ name }) => !name.toLowerCase().includes('test'));

    const countriesMap = this.getCountriesMap();

    Object.keys(countriesMap).forEach((ctryCode) => {
      // pick cities in the country
      const citiesOfCountry = cities
        .filter((city) => {
          if (ctryCode === REST_COUNTRIES_KEY) {
            // if the city doesn't belong to any country in the list, put it to others
            return !countriesMap[city.countryCode];
          }
          return city.countryCode === ctryCode;
        })
        .map((value) => {
          // create an array of tree nodes
          return {
            key: String(value.id),
            value: String(value.id),
            title: value.name,
            label: value.name,
          } as TreeData;
        });

      const countryValue =
        ctryCode === REST_COUNTRIES_KEY
          ? countriesMap[ctryCode].value
          : countriesMap[ctryCode];

      treeData.push({
        key: countryValue,
        value: countryValue,
        title: countryValue,
        children: citiesOfCountry,
        selectable: multiple,
      });
    });

    return treeData;
  }

  public getCitiesMap(): Map<number, City> {
    const citiesList = Service.cache!.getAllCities();
    const citiesMap = new Map();
    citiesList.forEach((c) => {
      citiesMap.set(c.id, c);
    });
    return citiesMap;
  }

  public clearCache(): void {
    localStorage.clear();
    sessionStorage.clear();
  }

  public clearServiceAndTableCache(): void {
    lscache.flush();
    this.clearTableCache();
    sessionStorage.clear();
  }

  private clearTableCache(): void {
    const keysToRemove: string[] = [];

    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key?.endsWith('_tableCustomizer')) {
        keysToRemove.push(key);
      }
    }

    keysToRemove.forEach((key) => localStorage.removeItem(key));
  }

  public setUserInfo(userInfo: ConcedoToken): void {
    datadogRum.setUser({
      id: userInfo.concedo_id,
      email: userInfo.email,
      clientId: userInfo.aud,
    });
    this.userInfo = userInfo;
  }

  public getUserInfo(): ConcedoToken | null {
    return this.userInfo;
  }

  public setSSOUserInfo(userInfo: GoogleInfo): void {
    this.ssoUserInfo = userInfo;
  }

  public getSSOUserInfo(): GoogleInfo | null {
    return this.ssoUserInfo;
  }
}
